import Progress from "../ValueObject/Progress";
import Status from "../ValueObject/Status";
import {
    Action,
    ADD_DOWNLOAD,
    ADD_CURRENT_DOWNLOAD,
    CANCEL_DOWNLOAD,
    CANCEL_CURRENT_DOWNLOAD,
    CHANGE_PROGRESS_DOWNLOAD,
    FINISH_DOWNLOAD,
    MOVE_DOWN_DOWNLOAD,
    MOVE_UP_DOWNLOAD,
    PAUSE_DOWNLOAD,
    PROCESS_DOWNLOAD,
    RESUME_DOWNLOAD
} from "../Action/Download";
import DocumentDownload from "../Entity/DocumentDownload";

export type DownloadState = {
    download: DocumentDownload[];
    currentDownload: DocumentDownload | null;
    closeDownload: DocumentDownload | null;
    progress: Progress;
};

const downloadState: DownloadState = {
    download: [],
    currentDownload: null,
    closeDownload: null,
    progress: new Progress(0),
};

const addDownload = (document: DocumentDownload, download: DocumentDownload[]): DocumentDownload[] => {
    const newDocuments: DocumentDownload[] = [...download];
    const documentFound: DocumentDownload | undefined = newDocuments.find(element => element.isEqual(document));
    if (documentFound instanceof DocumentDownload) {
        return newDocuments;
    }

    document.setStatus(Status.PENDING);

    return sortByPosition([...newDocuments, document]);
}

const getCurrentDownload = (download: DocumentDownload[]): DocumentDownload | null => {
    const documentSorted: DocumentDownload[] = sortByPosition(download);
    const documentFound: DocumentDownload | undefined = documentSorted.find(element => element.isPendingStatus());
    if (documentFound instanceof DocumentDownload) {
        return documentFound;
    }

    return null;
}

const pauseDownload = (state: DownloadState): DownloadState => {
    const currentDownload: DocumentDownload | null = state.currentDownload;
    if (null === currentDownload) {
        return state;
    }

    const documents: DocumentDownload[] = [...state.download];
    const documentFound: number = documents.findIndex(element => element.isEqual(currentDownload));
    if (-1 === documentFound) {
        return state;
    }

    currentDownload.setStatus(Status.PAUSED);
    documents[documentFound] = currentDownload;

    return {
        ...state,
        download: documents,
        currentDownload: currentDownload
    };
}

const resumeDownload = (state: DownloadState): DownloadState => {
    const currentDownload: DocumentDownload | null = state.currentDownload;
    if (null === currentDownload) {
        return state;
    }

    const documents: DocumentDownload[] = [...state.download];
    const documentFound: number = documents.findIndex(element => element.isEqual(currentDownload));
    if (-1 === documentFound) {
        return state;
    }

    currentDownload.setStatus(Status.DOWNLOADING);
    documents[documentFound] = currentDownload;

    return {
        ...state,
        download: documents,
        currentDownload: currentDownload
    };
}

const processDownload = (state: DownloadState): DownloadState => {
    const currentDownload: DocumentDownload | null = state.currentDownload;
    if (null === currentDownload) {
        return state;
    }

    const documents: DocumentDownload[] = [...state.download];
    const documentFound: number = documents.findIndex(element => element.isEqual(currentDownload));
    if (-1 === documentFound) {
        return state;
    }

    currentDownload.setStatus(Status.PROCESSING);
    documents[documentFound] = currentDownload;

    return {
        ...state,
        download: documents,
        currentDownload: null,
        closeDownload: currentDownload,
        progress: new Progress(0)
    };
}

const finishDownload = (state: DownloadState): DownloadState => {
    const closeDownload: DocumentDownload | null = state.closeDownload;
    if (null === closeDownload) {
        return state;
    }

    const documents: DocumentDownload[] = [...state.download];
    const documentFound: number = documents.findIndex(element => element.isEqual(closeDownload));
    if (-1 === documentFound) {
        return state;
    }

    documents.splice(documentFound, 1);

    return {
        ...state,
        download: sortDocuments(documents),
        currentDownload: null,
        closeDownload: null
    };
}

const cancelDownload = (id: number, download: DocumentDownload[]): DocumentDownload[] => {
    const documentFound: number = download.findIndex(element => id === element.getId());
    if (-1 === documentFound) {
        return download;
    }

    const documents: DocumentDownload[] = [...download];
    documents.splice(documentFound, 1);

    return sortDocuments(documents);
}

const cancelCurrentDownload = (state: DownloadState): DownloadState => {
    const download: DocumentDownload | null = state.currentDownload ?? state.closeDownload;
    if (null === download) {
        return state;
    }

    const documents: DocumentDownload[] = [...state.download];
    const documentFound: number = documents.findIndex(element => element.isEqual(download));
    if (-1 === documentFound) {
        return state;
    }

    documents.splice(documentFound, 1);

    return {
        ...state,
        download: sortDocuments(documents),
        currentDownload: null,
        closeDownload: null
    };
}

const sortDocuments = (download: DocumentDownload[]): DocumentDownload[] => {
    let position: number = 0;

    return download.map(document => {
        document.setPosition(position);
        position++;

        return document;
    });
}

const moveUpDownload = (id: number, download: DocumentDownload[]): DocumentDownload[] => {
    const documentFound: number = download.findIndex(element => id === element.getId());
    if (-1 === documentFound) {
        return download;
    }

    const currentPosition = download[documentFound].getPosition();
    const newPosition = currentPosition - 1;
    if (newPosition < 1) {
        return download;
    }

    return assignPositionAndSort(
        download,
        documentFound,
        newPosition,
        currentPosition
    );
}

const moveDownDownload = (id: number, download: DocumentDownload[]): DocumentDownload[] => {
    const documentFound: number = download.findIndex(element => id === element.getId());
    if (-1 === documentFound) {
        return download;
    }

    let currentPosition = download[documentFound].getPosition();
    if (0 === currentPosition) {
        return download;
    }

    const countElements = download.length;
    let newPosition = currentPosition + 1;
    if (newPosition >= countElements) {
        return download;
    }

    return assignPositionAndSort(
        download,
        documentFound,
        newPosition,
        currentPosition
    );
}

const assignPositionAndSort = (
    elements: DocumentDownload[],
    moveIndex: number,
    newPosition: number,
    currentPosition: number
) => {
    elements[moveIndex].setPosition(newPosition);

    const newElements: DocumentDownload[] = elements.map((document: DocumentDownload, index: number) => {
        if (newPosition === document.getPosition() && index !== moveIndex) {
            document.setPosition(currentPosition);
            return document;
        }

        return document;
    });

    return sortByPosition(newElements);
}

const sortByPosition = (documents: DocumentDownload[]) => {
    documents.sort((firstItem: DocumentDownload, secondItem: DocumentDownload): number => {
        return firstItem.getPosition() > secondItem.getPosition() ? 1 : -1;
    });

    return documents;
}

export default function download(state: DownloadState = downloadState, action: Action<any>): DownloadState {
    const {download, currentDownload} = state;

    switch (action.type) {
        case ADD_DOWNLOAD:
            return {
                ...state,
                download: addDownload(action.content, download)
            };
        case ADD_CURRENT_DOWNLOAD:
            return {
                ...state,
                currentDownload: currentDownload ?? getCurrentDownload(download),
                closeDownload: null
            };
        case PAUSE_DOWNLOAD:
            return {
                ...pauseDownload(state)
            };
        case RESUME_DOWNLOAD:
            return {
                ...resumeDownload(state)
            };
        case CANCEL_DOWNLOAD:
            return {
                ...state,
                download: cancelDownload(action.content, download)
            };
        case CANCEL_CURRENT_DOWNLOAD:
            return {
                ...cancelCurrentDownload(state)
            };
        case PROCESS_DOWNLOAD:
            return {
                ...processDownload(state)
            };
        case FINISH_DOWNLOAD:
            return {
                ...finishDownload(state)
            };
        case MOVE_UP_DOWNLOAD:
            return {
                ...state,
                download: moveUpDownload(action.content, download)
            }
        case MOVE_DOWN_DOWNLOAD:
            return {
                ...state,
                download: moveDownDownload(action.content, download)
            }
        case CHANGE_PROGRESS_DOWNLOAD:
            return {
                ...state,
                progress: action.content,
            }
        default:
            return state;
    }
}